/**
 * Agent Hub Page — Web UI cho Claude subagents (Phase 3 scaffold)
 *
 * - Sidebar: list 19 subagent (datahub-*, dev-*), nhóm theo tier.
 * - Main: chat area giống ChatGPT, gửi message tới agent đã chọn.
 * - Backend: /api/v1/agent-hub/agents (list), /api/v1/agent-hub/chat (POST).
 *
 * Roadmap:
 *  3.1: Stream response qua SSE (fetch with reader).
 *  3.2: Conversation history sidebar (group by date).
 *  3.3: Tool result rendering (code block, table, file diff).
 *  3.4: RBAC — chỉ admin thấy maintenance tier, dev role thấy dev tier.
 *
 * Phụ thuộc: axios instance đã có interceptor JWT (tìm trong utils/api hoặc tương đương).
 */
import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  Chip,
  Paper,
  CircularProgress,
  Alert,
} from '@mui/material';
import axios from 'axios';

// TODO: thay bằng axios instance chính của app (có JWT interceptor)
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api/v1',
});

const TIER_COLOR = {
  maintenance: 'warning',
  development: 'info',
  unknown: 'default',
};

export default function AgentHubPage() {
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    api
      .get('/agent-hub/agents')
      .then((res) => {
        if (!mounted) return;
        setAgents(res.data || []);
        if (res.data?.length) setSelectedAgent(res.data[0]);
      })
      .catch((e) => mounted && setError(e?.message || 'Cannot load agents'));
    return () => {
      mounted = false;
    };
  }, []);

  const handleSend = async () => {
    if (!input.trim() || !selectedAgent || loading) return;
    const userMsg = { role: 'user', content: input, ts: Date.now() };
    setMessages((m) => [...m, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const res = await api.post('/agent-hub/chat', {
        agent_name: selectedAgent.name,
        message: userMsg.content,
      });
      const reply = {
        role: 'assistant',
        agent: selectedAgent.name,
        content: res.data.reply,
        scaffold: res.data.scaffold_notice,
        ts: Date.now(),
      };
      setMessages((m) => [...m, reply]);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Chat failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAgent = (a) => {
    setSelectedAgent(a);
    setMessages([]); // Reset conversation khi đổi agent (Phase 3 minimal)
  };

  const maintenanceAgents = agents.filter((a) => a.tier === 'maintenance');
  const devAgents = agents.filter((a) => a.tier === 'development');

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 64px)' }}>
      {/* Sidebar */}
      <Paper sx={{ width: 320, overflow: 'auto', borderRadius: 0 }}>
        <Box sx={{ p: 2 }}>
          <Typography variant="h6">Agent Hub</Typography>
          <Typography variant="caption" color="text.secondary">
            {agents.length} agents available
          </Typography>
        </Box>
        <Divider />

        <Box sx={{ px: 2, pt: 2 }}>
          <Typography variant="overline">Maintenance ({maintenanceAgents.length})</Typography>
        </Box>
        <List dense>
          {maintenanceAgents.map((a) => (
            <ListItemButton
              key={a.name}
              selected={selectedAgent?.name === a.name}
              onClick={() => handleSelectAgent(a)}
            >
              <ListItemText
                primary={a.name}
                secondary={a.description?.slice(0, 80) + (a.description?.length > 80 ? '…' : '')}
              />
            </ListItemButton>
          ))}
        </List>

        <Divider />
        <Box sx={{ px: 2, pt: 2 }}>
          <Typography variant="overline">Development ({devAgents.length})</Typography>
        </Box>
        <List dense>
          {devAgents.map((a) => (
            <ListItemButton
              key={a.name}
              selected={selectedAgent?.name === a.name}
              onClick={() => handleSelectAgent(a)}
            >
              <ListItemText
                primary={a.name}
                secondary={a.description?.slice(0, 80) + (a.description?.length > 80 ? '…' : '')}
              />
            </ListItemButton>
          ))}
        </List>
      </Paper>

      {/* Main chat area */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {selectedAgent ? (
          <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="h6">{selectedAgent.name}</Typography>
            <Chip
              label={selectedAgent.tier}
              color={TIER_COLOR[selectedAgent.tier]}
              size="small"
              sx={{ mt: 0.5, mr: 1 }}
            />
            {selectedAgent.tools?.map((t) => (
              <Chip key={t} label={t} size="small" variant="outlined" sx={{ mr: 0.5, mt: 0.5 }} />
            ))}
            <Typography variant="body2" sx={{ mt: 1 }} color="text.secondary">
              {selectedAgent.description}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">Chọn 1 agent ở sidebar để bắt đầu</Typography>
          </Box>
        )}

        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ m: 2 }}>
            {error}
          </Alert>
        )}

        {/* Messages */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
          {messages.length === 0 && selectedAgent && (
            <Typography color="text.secondary" sx={{ textAlign: 'center', mt: 4 }}>
              Bắt đầu cuộc hội thoại với <b>{selectedAgent.name}</b>
            </Typography>
          )}
          {messages.map((m, i) => (
            <Box
              key={i}
              sx={{
                mb: 2,
                display: 'flex',
                justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start',
              }}
            >
              <Paper
                sx={{
                  p: 1.5,
                  maxWidth: '75%',
                  bgcolor: m.role === 'user' ? 'primary.main' : 'grey.100',
                  color: m.role === 'user' ? 'primary.contrastText' : 'text.primary',
                }}
              >
                {m.role === 'assistant' && m.agent && (
                  <Typography variant="caption" sx={{ opacity: 0.7 }}>
                    {m.agent}
                  </Typography>
                )}
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {m.content}
                </Typography>
                {m.scaffold && (
                  <Typography variant="caption" sx={{ display: 'block', mt: 1, fontStyle: 'italic', opacity: 0.7 }}>
                    {m.scaffold}
                  </Typography>
                )}
              </Paper>
            </Box>
          ))}
          {loading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
              <CircularProgress size={16} />
              <Typography variant="caption">Agent đang trả lời...</Typography>
            </Box>
          )}
        </Box>

        {/* Input */}
        {selectedAgent && (
          <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider', display: 'flex', gap: 1 }}>
            <TextField
              fullWidth
              size="small"
              placeholder={`Gửi message tới ${selectedAgent.name}…`}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              disabled={loading}
              multiline
              maxRows={4}
            />
            <Button variant="contained" onClick={handleSend} disabled={loading || !input.trim()}>
              Send
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
}
